<script setup lang="ts">
import { onMounted, ref, PropType, watch } from "vue";
import { Participant, Project, ProjectRole } from "../../types/PersologOnlineTypes";
import { fetchDefaultRoleNames, fetchRoles, fetchRoleTypes, saveRoles } from "../../utils/ProjectUtils";
import { useI18n } from "vue-i18n";
import RoleItem from "./RoleItem.vue";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

/**
 * Event emits
 */
const emit = defineEmits(["update:roles"]);

const collapsed = ref(true);

/**
 * Becomes true once the initial roles + selection state are loaded from the backend.
 */
const isInitialized = ref(false);

/**
 * Controls the state of each role in the list
 */
const rolesList = ref<
    { id: number; type: string; name: string; selected: boolean; toggleable: boolean; editMode: boolean }[]
>([]);

const defaultRoleNames = ref<Record<string, string>>({});

const getDefaultRoleName = (roleType: string): string => {
    return (
        defaultRoleNames.value[roleType] ??
        defaultRoleNames.value[roleType.toLowerCase()] ??
        defaultRoleNames.value[roleType.toUpperCase()] ??
        t(`project-configuration.roles.${roleType.toLowerCase()}`)
    );
};

/**
 * Roles selected for the project
 */
const selectedRoles = ref<ProjectRole[]>([]);

const isRoleSelected = (roleType: string) =>
    selectedRoles.value.some(role => role.role === roleType);

/**
 * Check if a role can be toggled (selected/deselected)
 * A role cannot be toggled if it has participants assigned to it
 */
const isRoleToggleable = (roleType: string) => {
    return !props.participants.some(participant =>
        participant.roleId !== 0 &&
        selectedRoles.value.some(role =>
            role.id === participant.roleId &&
            role.role === roleType
        )
    );
};

/**
 * Controls if we're in edit mode for role names
 */
const isEditingNames = ref<boolean>(false);

/**
 * Store original role names for cancellation
 */
const originalRoleNames = ref<Record<string, string>>({});

/**
 * Handle role toggle from RoleItem component
 */
async function handleRoleToggle(role: { type: string; name: string; selected: boolean; toggleable: boolean }) {
    await toggleRole(role);
}

/**
 * Handle role name update from RoleItem component
 */
function handleRoleNameUpdate(roleType: string, newName: string) {
    const role = rolesList.value.find(r => r.type === roleType);
    if (role) {
        role.name = newName;
    }
}

/**
 * Toggle role availability
 */
async function toggleRole(role: { type: string; name: string; selected: boolean; toggleable: boolean }) {
    if (!role.toggleable) return;

    if (role.selected) {
        // Remove from selectedRoles
        selectedRoles.value = selectedRoles.value.filter(r => r.role !== role.type);
    } else {
        // Add to selectedRoles
        selectedRoles.value.push({
            id: 0,
            role: role.type,
            name: role.name
        });
    }

    // Update rolesList selected status
    role.selected = !role.selected;

    await saveRolesToBackend();
}

/**
 * Enter edit mode for role names
 */
function startEditingNames() {
    // Store original names
    rolesList.value.forEach(role => {
        originalRoleNames.value[role.type] = role.name;
    });
    isEditingNames.value = true;
}

/**
 * Save name changes and exit edit mode
 */
async function saveNameChanges() {
    await saveRolesToBackend();
    isEditingNames.value = false;
}

/**
 * Cancel name changes and restore original names
 */
function cancelNameChanges() {
    // Restore original names
    rolesList.value.forEach(role => {
        if (originalRoleNames.value[role.type]) {
            role.name = originalRoleNames.value[role.type];
        }
    });
    isEditingNames.value = false;
}

/**
 * Initializes the rolesList with the available roles
 */
async function initRolesList() {
    defaultRoleNames.value = await fetchDefaultRoleNames(props.project);
    const allAvailableRolesForProduct = await fetchRoleTypes(props.project?.product);
    rolesList.value = allAvailableRolesForProduct.map((role) => {
        return {
            id: 0,
            type: role.type,
            name: getDefaultRoleName(role.type),
            selected: true,
            toggleable: true,
            editMode: false,
        };
    });
}

/**
 * Syncs the UI rolesList display with the current selectedRoles data
 * Updates: id, selected status, name, and toggleable status based on participants
 * Direction: Data → UI
 */
function syncRolesListFromData() {
    rolesList.value = rolesList.value.map((roleListItem) => {
        const matchingRole = selectedRoles.value.find(r => r.role === roleListItem.type);

        return {
            ...roleListItem,
            id: matchingRole?.id || 0,
            selected: isRoleSelected(roleListItem.type),
            name: matchingRole?.name || getDefaultRoleName(roleListItem.type),
            toggleable: isRoleToggleable(roleListItem.type)
        };
    });
}

/**
 * Saves UI changes to the data layer and persists to backend
 * Builds selectedRoles from current rolesList state and saves to project
 * Direction: UI → Data → Backend
 */
async function saveRolesToBackend() {
    // Update rolesList selection status using isRoleSelected
    rolesList.value.forEach((role) => {
        role.selected = isRoleSelected(role.type);
    });

    // Build selectedRoles from rolesList
    selectedRoles.value = rolesList.value
        .filter((role) => role.selected)
        .map((role) => {
            // Preserve existing id if role was already selected
            const existingRole = selectedRoles.value.find((selectedRole) => selectedRole.role === role.type);
            return {
                id: existingRole?.id ?? role.id ?? 0,
                role: role.type,
                name: role.name
            } as ProjectRole;
        });

    emit("update:roles", selectedRoles.value);
    await saveRoles(props.project, selectedRoles.value);
}

/**
 * Fetch the roles for the project and set the selected roles
 */
onMounted(async () => {
    await initRolesList();
    selectedRoles.value = await fetchRoles(props.project);
    syncRolesListFromData();
    isInitialized.value = true;
});

watch(
    () => props.participants,
    () => {
        syncRolesListFromData();
    },
);
</script>

<template>
    <Panel
        :id="`roles_panel-${project.id}`"
        toggleable
        v-model:collapsed="collapsed"
        class="mt-4"
        :pt="{ header: { onClick: () => { collapsed = !collapsed; }, style: 'cursor: pointer' }, pcToggleButton: { root: { onClick: (e: MouseEvent) => e.stopPropagation() } } }"
    >
        <template #header>
            <div class="flex items-center">
                <span class="m-0 p-panel-title">{{ t("project-configuration.roles.label") }}</span>
                <i
                    class="pi pi-info-circle ml-2 align-center"
                    v-tooltip.right="t(`project-configuration.roles.description.${project.product}`)"
                ></i>
            </div>
        </template>
    <Suspense>
        <div>
            <div class="roles-container" v-if="isInitialized">
                <!-- Role chips and edit button inline -->
                <div class="roles-list">
                    <RoleItem
                        v-for="role in rolesList"
                        :key="role.type"
                        :role="role"
                        :project-id="project.id || 0"
                        :project-product="project.product"
                        :is-editing-names="isEditingNames"
                        @toggle="handleRoleToggle"
                        @update-name="handleRoleNameUpdate"
                        class="m-0 mb-1"
                    />

                    <!-- Edit/Save button inline with roles -->
                    <div class="edit-button-container">
                        <Button
                            v-if="!isEditingNames"
                            :id="`edit-names-btn-${project.id}`"
                            icon="pi pi-pencil"
                            size="normal"
                            text
                            severity="secondary"
                            @click="startEditingNames"
                            :aria-label="t('buttons.editName')"
                            v-tooltip.top="t('buttons.editName')"
                            class="edit-names-btn mb-1"
                        />
                        <div v-else class="flex gap-2">
                            <Button
                                :id="`save-names-btn-${project.id}`"
                                icon="pi pi-check"
                                size="small"
                                severity="success"
                                @click="saveNameChanges"
                                :aria-label="t('buttons.save')"
                                v-tooltip.top="t('buttons.save')"
                                class="save-btn mb-1"
                            />
                            <Button
                                :id="`cancel-edit-names-btn-${project.id}`"
                                icon="pi pi-times"
                                size="small"
                                severity="danger"
                                @click="cancelNameChanges"
                                :aria-label="t('buttons.cancel')"
                                v-tooltip.top="t('buttons.cancel')"
                                class="cancel-btn mb-1"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Suspense>
    </Panel>
</template>

<style scoped>
/* Layout helpers */
.roles-container {
    display: flex;
    flex-direction: column;
}

.roles-list {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
}

.edit-button-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.edit-names-btn,
.save-btn,
.cancel-btn {
    padding: 0.375rem;
    min-width: auto;
    border: 1px solid var(--p-secondary-500) !important;
    --p-button-success-background: var(--p-secondary-500);    /* Custom green */
    --p-button-success-hover-background: var(--p-secondary-900);
}

.edit-names-btn {
    background-color: white !important;
    color: var(--p-secondary-500) !important;
}

.edit-names-btn:hover {
    background-color: var(--p-secondary-50) !important;
}
</style>
